import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';

import CompareMode from '@/components/CompareMode';
import { RightProvider } from '@/components/RightProvider';
import en from '@/lang/en.json';
import zh from '@/lang/zh.json';
import theme from '@/theme';
import { generateZiseFromRadio } from '@/utils/common';

import Loading from '../Loading';
import { useResize } from '../MoveContainer/hooks/useResize';
import ImagePreview from '.';
import { useImageLoading } from './hooks/useImageLoading';
import { useSplitDrag } from './hooks/useSplitDrag';
import styles from './index.module.scss';

interface ImagePreviewShowOptions {
  src: string;
  title?: string;
  onClose?: () => void;
}

const ProxyImagePreview: React.FC<{
  options: ImagePreviewShowOptions;
  destroyHandle: () => void;
}> = ({ options, destroyHandle }) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    options.onClose?.();
    setTimeout(() => {
      destroyHandle();
    }, 300);
  };

  if (!visible) return null;

  return <ImagePreview src={options.src} title={options.title} onClose={handleClose} />;
};

export const ImagePreviewImperative = {
  show: (options: ImagePreviewShowOptions) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let isDestroyed = false;

    const destroyHandle = () => {
      if (isDestroyed) return;
      isDestroyed = true;

      try {
        root.unmount();
      } catch (error) {
        console.warn('Failed to unmount root:', error);
      }

      try {
        if (container.parentNode === document.body) {
          document.body.removeChild(container);
        }
      } catch (error) {
        console.warn('Failed to remove container:', error);
      }
    };

    const lang = navigator.language.startsWith('zh') ? 'zh' : 'en';

    root.render(
      <RightProvider>
        <IntlProvider locale={lang} messages={lang === 'en' ? en : zh}>
          <ConfigProvider theme={theme}>
            <StyleProvider hashPriority="high">
              <ProxyImagePreview options={options} destroyHandle={destroyHandle} />
            </StyleProvider>
          </ConfigProvider>
        </IntlProvider>
      </RightProvider>,
    );

    return destroyHandle;
  },
};
interface ImagePreviewCompareOptions {
  src: string;
  beforeImgUrl: string;
  title?: string;
  onClose?: () => void;
  destroyHandle?: () => void;
}

const ImagePreviewCompare = (options: ImagePreviewCompareOptions) => {
  const previewContentRef = useRef<HTMLDivElement>(null);
  const [previewContentSize, setPreviewContentSize] = useState({
    width: 0,
    height: 0,
  });

  const { splitPos, startDrag, containerRef } = useSplitDrag({
    containerWidth: previewContentSize.width,
    isEnabled: true,
  });

  const { beforeImgUrl, destroyHandle, src, onClose } = options;

  const { isLoading, handleBeforeImageLoad, handleAfterImageLoad } = useImageLoading();

  const compareOptions = {
    beforeImgUrl,
    afterImgUrl: src,
    splitPos,
    startDrag,
    isLoading,
    previewContentSize,
    onBeforeImageLoad: handleBeforeImageLoad,
    onAfterImageLoad: handleAfterImageLoad,
  };

  const handleClose = () => {
    onClose?.();
    setTimeout(() => {
      destroyHandle?.();
    }, 300);
  };

  // 获取图片实际尺寸
  const getImageNaturalSize = useCallback(
    (imageUrl: string): Promise<{ width: number; height: number }> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = reject;
        img.src = imageUrl;
      });
    },
    [],
  );

  // 计算预览尺寸
  const calculatePreviewSize = async (
    containerWidth: number,
    containerHeight: number,
  ) => {
    let ratio: number;

    // 没有分辨率信息，需要加载图片获取
    try {
      const naturalSize = await getImageNaturalSize(src);
      ratio = naturalSize.width / naturalSize.height;
    } catch (error) {
      console.error('Failed to load image:', error);
      // 加载失败时，使用容器尺寸
      setPreviewContentSize({ width: containerWidth, height: containerHeight });
      return;
    }
    const size = generateZiseFromRadio(ratio, {
      width: containerWidth,
      height: containerHeight,
    });
    setPreviewContentSize(size);
  };

  const resizeCallback = ({ width, height }: { width: number; height: number }) => {
    calculatePreviewSize(width, height);
  };

  useResize(previewContentRef, resizeCallback);

  useEffect(() => {
    // 重置图片自然尺寸
    const containerWidth = previewContentRef.current?.offsetWidth || 0;
    const containerHeight = previewContentRef.current?.offsetHeight || 0;
    calculatePreviewSize(containerWidth, containerHeight);
  }, []);

  return (
    <div className={styles.compareModalOverlay} onClick={handleClose}>
      {/* 弹窗容器 */}
      <div className={styles.compareModalDialog} onClick={(e) => e.stopPropagation()}>
        {/* 标题栏 */}
        <div className={styles.compareModalHeader}>
          <span className={styles.compareModalTitle}>Preview</span>
          {/* 关闭按钮 */}
          <div className={styles.compareModalCloseBtn} onClick={handleClose}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path
                d="M17 5L5 17M5 5L17 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* 内容区域 */}
        <div className={styles.compareModalBody}>
          {/* 图片容器 */}
          <div ref={previewContentRef} className={styles.compareModalImageWrapper}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <Loading size={40} iconColor="#ffffff" />
              </div>
            )}
            <div
              ref={containerRef}
              className="relative select-none"
              style={{
                width: previewContentSize.width,
                height: previewContentSize.height,
              }}
            >
              <CompareMode {...compareOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ImagePreviewCompareImperative = {
  show: (options: ImagePreviewCompareOptions) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let isDestroyed = false;

    const destroyHandle = () => {
      if (isDestroyed) return;
      isDestroyed = true;

      try {
        root.unmount();
      } catch (error) {
        console.warn('Failed to unmount root:', error);
      }

      try {
        if (container.parentNode === document.body) {
          document.body.removeChild(container);
        }
      } catch (error) {
        console.warn('Failed to remove container:', error);
      }
    };

    const lang = navigator.language.startsWith('zh') ? 'zh' : 'en';

    root.render(
      <RightProvider>
        <IntlProvider locale={lang} messages={lang === 'en' ? en : zh}>
          <ConfigProvider theme={theme}>
            <StyleProvider hashPriority="high">
              <ImagePreviewCompare {...options} destroyHandle={destroyHandle} />
            </StyleProvider>
          </ConfigProvider>
        </IntlProvider>
      </RightProvider>,
    );

    return destroyHandle;
  },
};
